import { GeocodeAddress, MapboxPlaceType } from '@citruscamps/citrus-client'
import React, { useCallback } from 'react'
import { SupportedCountries } from '../constants/application'
import { HttpClient } from '../utils/http'
import { toQueryParams } from '../utils/query-params'

const MapboxAccessToken: string = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN || ''

export interface GeocoderResponse {
  type: string
  query: string[]
  features: GeocodeAddress[]
  attribution: string
  search_time: Date
}

interface ISearchProps {
  proximity?: number[]
  bbox?: string
  types?: MapboxPlaceType[]
  value: string
  limit?: number
}

export const search = async ({
  proximity,
  bbox,
  types,
  value,
  limit = 4,
}: ISearchProps): Promise<GeocoderResponse | undefined> => {
  if (!value) {
    return undefined
  }
  const source: string = 'mapbox.places'
  const search_time = new Date()
  const country: string = SupportedCountries.join(',')
  const query: string = toQueryParams({
    access_token: MapboxAccessToken,
    proximity: proximity?.join(','),
    bbox,
    types: types?.join(','),
    country,
    limit,
  })
  const baseUrl: string = 'https://api.tiles.mapbox.com'
  const uri: string = `/geocoding/v5/${source}/${encodeURIComponent(value)}.json?${query}`
  const http = new HttpClient({ baseUrl })
  try {
    const result: GeocoderResponse = await http.get<GeocoderResponse>(uri)
    return {
      ...result,
      search_time,
    }
  } catch {
    return undefined
  }
}

function debounceSearch(
  func: React.ChangeEventHandler<HTMLInputElement>,
  searchFunc: (value: string) => void | Promise<void>,
  wait: number,
): React.ChangeEventHandler<HTMLInputElement> {
  let timeout: number | undefined
  return function (evt: React.ChangeEvent<HTMLInputElement>): void {
    const { value } = evt.target || {}
    func(evt)
    if (timeout) window.clearTimeout(timeout)
    timeout = window.setTimeout(() => {
      timeout = undefined
      searchFunc(value)
    }, wait)
  }
}

interface IValue {
  handleDebounceChange: React.ChangeEventHandler<HTMLInputElement>
}

interface IProps {
  proximity?: number[]
  bbox?: string
  types?: MapboxPlaceType[]
  limit?: number
  onChange: React.ChangeEventHandler<HTMLInputElement>
  onSuggest: (value: GeocoderResponse | undefined) => void | Promise<void>
}

export const useGeocoderSearch = ({
  proximity,
  bbox,
  types,
  limit,
  onChange: handleChange,
  onSuggest: handleSuggest,
}: IProps): IValue => {
  const handleSearch = async (value: string) => {
    const results: GeocoderResponse | undefined = await search({
      proximity,
      bbox,
      types,
      limit,
      value,
    })
    handleSuggest(results)
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleDebounceChange = useCallback(debounceSearch(handleChange, handleSearch, 700), [])
  return {
    handleDebounceChange,
  }
}
